import { useCallback, useState } from "react";
import { useParams } from "react-router-dom";

import { ClinicalImpression } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Box, Button, Card, Flex, Stack } from "@mantine/core";

import {
  ClientNote,
  ClientNoteStatus,
  updateClientNoteStatus,
} from "@/clients/zod-schemas/client-note";
import { TextWrapper } from "@/components/TextWrapper";

import { useClientNotes } from "../utils/hooks";

import ArchiveClientReminderNote, { ArchiveModalProps } from "./ArchiveClientReminderNote";
import EditClientReminderNote from "./EditClientReminderNote";

export default function DisplayClientReminderNote({
  openModal,
  modalHandlers,
  refresh,
  setRefresh,
}: ArchiveModalProps) {
  const [expanded, setExpanded] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [filingNoteId, setFilingNoteId] = useState<string | null>(null);
  const medplum = useMedplum();
  const { clientId } = useParams<{ clientId: string }>();
  const status = ClientNoteStatus.InProgress;

  const reminderNotes = useClientNotes(clientId, medplum, status, refresh, ClientNote.Reminder);

  const handleFileReminder = useCallback(
    async (note: ClinicalImpression) => {
      if (filingNoteId) return;
      setFilingNoteId(note.id || null);
      try {
        await updateClientNoteStatus({
          medplum,
          clinicalImpression: note,
          status: ClientNoteStatus.Completed,
        });
        setRefresh((prev) => !prev);
      } finally {
        setFilingNoteId(null);
      }
    },
    [medplum, setRefresh, filingNoteId],
  );

  // Show only first note (truncated) when collapsed, all notes when expanded
  const notesToDisplay = expanded ? reminderNotes : reminderNotes.slice(0, 1);
  const hasMultipleNotes = reminderNotes.length > 1;

  return (
    <Card px={0}>
      <Box
        style={{
          maxHeight: expanded ? 200 : undefined,
          overflowY: expanded ? "auto" : undefined,
          overflowX: "hidden",
          scrollbarGutter: expanded ? "stable" : undefined,
        }}
      >
        <Stack gap="md">
          {notesToDisplay.map((note, index) => {
            const isEditing = editingNoteId === note.id;
            // Only truncate the first note when collapsed
            const shouldTruncate = !expanded && index === 0;

            if (isEditing) {
              return (
                <EditClientReminderNote
                  key={note.id}
                  setRefresh={setRefresh}
                  clinicalImpression={note}
                  handleToggleEdit={() => setEditingNoteId(null)}
                />
              );
            }

            return (
              <div key={note.id}>
                <TextWrapper size="sm" lineClamp={shouldTruncate ? 2 : undefined}>
                  {note.summary}
                </TextWrapper>
                <Flex mt="xs" pr="sm" gap="xs" align="center" justify="flex-end">
                  <Button
                    color="black"
                    variant="subtle"
                    size="compact-xs"
                    onClick={() => setEditingNoteId(note.id || null)}
                  >
                    Edit
                  </Button>
                  <Button
                    color="black"
                    variant="outline"
                    size="compact-xs"
                    disabled={filingNoteId !== null}
                    loading={filingNoteId === note.id}
                    onClick={() => handleFileReminder(note)}
                  >
                    File
                  </Button>
                </Flex>
              </div>
            );
          })}
        </Stack>
      </Box>
      {hasMultipleNotes && (
        <Flex mt="xs" justify="flex-start">
          <Button
            pl={0}
            color="black"
            variant="subtle"
            size="compact-xs"
            onClick={() => setExpanded(!expanded)}
          >
            <TextWrapper size="sm" td="underline">
              {expanded ? "See Less" : "See More"}
            </TextWrapper>
          </Button>
        </Flex>
      )}
      <ArchiveClientReminderNote
        refresh={refresh}
        openModal={openModal}
        setRefresh={setRefresh}
        modalHandlers={modalHandlers}
      />
    </Card>
  );
}
